import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useSessionStore } from '@features/auth/store/useSessionStore';
import { logError } from '@lib/logger/logError';
import { logInfo } from '@lib/logger/logInfo';
import { logSuccess } from '@lib/logger/logSuccess';

import useSessionsApi from '@features/wizard/hooks/useSessionsApi';
import useWizardStore from '@features/wizard/store/useWizardStore';
import {
  SESSION_CONTACT_STATUSES,
  type SdrCallSessionSummary,
  type SessionContactItem,
  type SessionContactStatus,
  type SessionStatusCounts,
} from '@features/wizard/types/wizard.types';

const CHUNK_SIZE = 50;

/** Collapses contacts sharing a phone number into a single row, keeping the
 *  first occurrence and tagging it with `duplicateCount` (total times that phone
 *  appeared across the selected lists/buckets). Contacts without a phone are
 *  kept as-is and never treated as duplicates. */
const dedupeByPhone = (contacts: SessionContactItem[]): SessionContactItem[] => {
  const counts = new Map<string, number>();
  contacts.forEach((contact) => {
    const key = contact.phone?.trim() ?? '';
    if (key !== '') {
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
  });

  const seen = new Set<string>();
  const result: SessionContactItem[] = [];
  contacts.forEach((contact) => {
    const key = contact.phone?.trim() ?? '';
    if (key === '') {
      result.push(contact);
      return;
    }
    if (seen.has(key)) {
      return;
    }
    seen.add(key);
    result.push({ ...contact, duplicateCount: counts.get(key) ?? 1 });
  });
  return result;
};

const chunked = <T, >(items: T[], size: number): T[][] => {
  const chunks: T[][] = [];
  for (let i = 0; i < items.length; i += size) {
    chunks.push(items.slice(i, i + size));
  }
  return chunks;
};

export interface UseSessionsViewModelReturn {
  /** SDR's past sessions (left column) */
  sessions: SdrCallSessionSummary[];
  isLoadingSessions: boolean;

  /** Status counts across selected sessions (chip counters) */
  statusCounts: SessionStatusCounts;
  isLoadingCounts: boolean;

  /** Filtered + paginated contacts for the preview table */
  paginatedContacts: SessionContactItem[];
  totalFilteredContacts: number;
  isLoadingPreview: boolean;

  /** Persists the filtered selection as a brand-new contact list. */
  createListFromSelection: () => Promise<string | null>;

  /** Derived flags */
  canProceed: boolean;
  hasSelection: boolean;
}

export default function useSessionsViewModel (): UseSessionsViewModelReturn {
  const currentUser = useSessionStore((state) => state.currentUser);
  const sdrId = String(currentUser?.sdrId ?? currentUser?.userId ?? '').trim();
  const organizationId = String(currentUser?.organizationId ?? '').trim();

  const sessionsApi = useSessionsApi();

  const {
    sessionsTab,
  } = useWizardStore();

  const {
    selectedSessionIds,
    activeFilters,
    newListName,
    currentPage,
    pageSize,
  } = sessionsTab;

  const [sessions, setSessions] = useState<SdrCallSessionSummary[]>([]);
  const [isLoadingSessions, setIsLoadingSessions] = useState(false);

  /** Holds the 5 per-status arrays for the currently selected sessions.
   *  Single source of truth for counts AND preview — no race conditions.
   */
  const [contactsByStatus, setContactsByStatus] = useState<Record<SessionContactStatus, SessionContactItem[]>>({
    noAnswer: [],
    cutOff: [],
    transferError: [],
    voicemail: [],
    fuegoLead: [],
  });
  const [isLoadingCounts, setIsLoadingCounts] = useState(false);

  /** Avoid race conditions when selection changes rapidly */
  const lastSelectionTokenRef = useRef(0);

  const refreshSessions = useCallback(async () => {
    if (sdrId === '' || organizationId === '') {
      return;
    }
    setIsLoadingSessions(true);
    const result = await sessionsApi.listSdrSessionsAsync(sdrId, organizationId);
    if (result.success) {
      setSessions(result.data);
    } else {
      setSessions([]);
    }
    setIsLoadingSessions(false);
  }, [sdrId, organizationId, sessionsApi]);

  useEffect(() => {
    void refreshSessions();
  }, [refreshSessions]);

  /** Fetch the 5 per-status arrays once per selection change.
   *  Each call is cached server-side; chip counts and preview both derive from this.
   */
  useEffect(() => {
    if (selectedSessionIds.length === 0) {
      setContactsByStatus({
        noAnswer: [],
        cutOff: [],
        transferError: [],
        voicemail: [],
        fuegoLead: [],
      });
      return;
    }
    const token = ++lastSelectionTokenRef.current;
    setIsLoadingCounts(true);

    const fetchPerStatus = async (
      status: SessionContactStatus,
    ): Promise<[SessionContactStatus, SessionContactItem[]]> => {
      const result = await sessionsApi.fetchSessionContactsAsync(selectedSessionIds, [status]);
      return [status, result.success ? result.data : []];
    };

    void Promise.all(SESSION_CONTACT_STATUSES.map(fetchPerStatus))
      .then((entries) => {
        if (token !== lastSelectionTokenRef.current) {
          return;
        }
        const next = entries.reduce<Record<SessionContactStatus, SessionContactItem[]>>((acc, [status, contacts]) => {
          acc[status] = contacts;
          return acc;
        }, {
          noAnswer: [],
          cutOff: [],
          transferError: [],
          voicemail: [],
          fuegoLead: [],
        });
        setContactsByStatus(next);
        setIsLoadingCounts(false);
      })
      .catch((error: unknown) => {
        void logError('Failed to fetch session status counts.', 'Sessions.viewModel.counts', { error });
        setIsLoadingCounts(false);
      });
  }, [selectedSessionIds, sessionsApi]);

  /** Chip counts reflect raw appearances per disposition (a contact with multiple
   *  actions appears in multiple chip counts — pending product decision).
   */
  const statusCounts = useMemo<SessionStatusCounts>(() => ({
    noAnswer: contactsByStatus.noAnswer.length,
    cutOff: contactsByStatus.cutOff.length,
    transferError: contactsByStatus.transferError.length,
    voicemail: contactsByStatus.voicemail.length,
    fuegoLead: contactsByStatus.fuegoLead.length,
  }), [contactsByStatus]);

  /** Preview = union of active-filter buckets, deduplicated by phone so each
   *  contact appears once even when present in multiple buckets.
   */
  const previewContacts = useMemo<SessionContactItem[]>(() => {
    if (selectedSessionIds.length === 0) {
      return [];
    }
    const statusesToInclude = activeFilters.length === 0
      ? SESSION_CONTACT_STATUSES
      : activeFilters;
    const tagged: SessionContactItem[] = [];
    statusesToInclude.forEach((status) => {
      contactsByStatus[status].forEach((contact) => {
        tagged.push({ ...contact, disposition: status });
      });
    });
    return dedupeByPhone(tagged);
  }, [selectedSessionIds, activeFilters, contactsByStatus]);

  const totalFilteredContacts = previewContacts.length;

  const paginatedContacts = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return previewContacts.slice(startIndex, startIndex + pageSize);
  }, [previewContacts, currentPage, pageSize]);

  const hasSelection = selectedSessionIds.length > 0;
  const canProceed = hasSelection && newListName.trim().length > 0 && totalFilteredContacts > 0;

  const createListFromSelection = useCallback(async (): Promise<string | null> => {
    if (!canProceed || organizationId === '') {
      return null;
    }
    const title = newListName.trim();

    const listResult = await sessionsApi.createContactListAsync({
      organizationId,
      title,
      type: 'CSV',
      nonce: 'CSV',
    });

    if (!listResult.success) {
      void logError('Could not create contact list from sessions.', 'Sessions.viewModel.createList', {
        title,
        organizationId,
      });
      return null;
    }

    const newListId = listResult.data.id;
    const chunks = chunked(previewContacts, CHUNK_SIZE);

    for (const chunk of chunks) {
      const stripped = chunk.map(({ id: _id, ...rest }) => rest);
      const chunkResult = await sessionsApi.addContactsToListAsync({
        contactListId: newListId,
        organizationId,
        contacts: stripped,
      });
      if (!chunkResult.success) {
        void logError('Failed to upload contacts chunk to new list.', 'Sessions.viewModel.createList', {
          contactListId: newListId,
          chunkSize: chunk.length,
        });
        return null;
      }
    }

    void logInfo('Sessions list created.', 'Sessions.viewModel.createList', {
      contactListId: newListId,
      total: previewContacts.length,
    });
    void logSuccess('New contact list saved.', 'Sessions.viewModel.createList', {
      contactListId: newListId,
      title,
    });

    return newListId;
  }, [canProceed, organizationId, newListName, previewContacts, sessionsApi]);

  return {
    sessions,
    isLoadingSessions,
    statusCounts,
    isLoadingCounts,
    paginatedContacts,
    totalFilteredContacts,
    isLoadingPreview: isLoadingCounts,
    createListFromSelection,
    canProceed,
    hasSelection,
  };
}
